// LAB-ONLY candidate alternates for the Brutality ("Smash!") finisher —
// previewed on /vfx-lab for approval, NOT wired into the game. Same uniform
// contract as brutality.frag.ts (uTime / uDuration / uAspect / uCenter).

// Slam! — a piledriver: a white light slab drops from above, detonates on
// impact (hard flash), then a flattened ground shockwave races out while
// sector-hashed debris sparks kick upward.
export const BRUTALITY_SLAM_FRAG = /* glsl */ `
  precision mediump float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uDuration;
  uniform vec2 uAspect;
  uniform vec2 uCenter;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  void main() {
    vec2 uv = ((vUv - uCenter) * 2.0) * uAspect;
    float r = length(uv);
    float a = atan(uv.y, uv.x);
    float t = clamp(uTime / uDuration, 0.0, 1.0);

    // Slab falls over the first 26%, then fades out after impact.
    float dropP = smoothstep(0.0, 0.26, t);
    float slabY = mix(1.15, 0.0, dropP);
    float slabCore = 1.0 - smoothstep(0.1, 0.13, abs(uv.x));
    float slabGlow = 1.0 - smoothstep(0.13, 0.3, abs(uv.x));
    float yMask = smoothstep(slabY - 0.02, slabY + 0.04, uv.y)
      * (1.0 - smoothstep(slabY + 0.95, slabY + 1.05, uv.y));
    float slab = (slabCore + slabGlow * 0.35) * yMask * (1.0 - smoothstep(0.3, 0.5, t));

    float hit = step(0.26, t);
    float postT = max(t - 0.26, 0.0);
    float flash = hit * exp(-11.0 * postT);

    // Ground shockwave — ellipse metric flattens the ring onto the impact line.
    float er = length(vec2(uv.x, uv.y * 3.5));
    float wave = (1.0 - smoothstep(0.0, 0.05, abs(er - postT * 3.2)))
      * hit * (1.0 - smoothstep(0.55, 0.95, t));

    // Upward debris sparks. Sector index uses mod() so the -x seam lands on a
    // cell boundary — no visible seam line.
    float cells = 12.0;
    float sa = (a / 6.2831853 + 0.5) * cells;
    float seg = mod(floor(sa), cells);
    float fr = fract(sa) - 0.5;
    float h = hash(vec2(seg, 3.0));
    float sr = postT * (1.5 + 1.2 * h);
    float spark = exp(-pow((r - sr) * 9.0, 2.0))
      * (1.0 - smoothstep(0.0, 0.12, abs(fr)))
      * step(0.05, uv.y) * hit * exp(-2.2 * postT);

    vec3 white = vec3(1.0);
    vec3 mint = vec3(0.063, 0.957, 0.545);
    vec3 col = white * (flash + slab)
      + mix(white, mint, 0.5) * wave * 1.2
      + mix(mint, white, 0.4) * spark * 1.3;
    float alpha = clamp(flash + slab + wave + spark * 1.2, 0.0, 1.0);
    gl_FragColor = vec4(col, alpha);
  }
`

// Shatter! — the screen blows apart into 14 glass shards that fly outward,
// each with its own speed, width, spin drift, and glint flicker, leaving a
// mint dust trail. Sector-based (mod on the index) so there's no atan seam.
export const BRUTALITY_SHATTER_FRAG = /* glsl */ `
  precision mediump float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uDuration;
  uniform vec2 uAspect;
  uniform vec2 uCenter;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  void main() {
    vec2 uv = ((vUv - uCenter) * 2.0) * uAspect;
    float r = length(uv);
    float a = atan(uv.y, uv.x);
    float t = clamp(uTime / uDuration, 0.0, 1.0);
    float tt = uTime;

    float flash = exp(-9.0 * t);

    float sectors = 14.0;
    float sa = (a / 6.2831853 + 0.5) * sectors;
    float seg = mod(floor(sa), sectors);
    float fr = fract(sa);
    float h1 = hash(vec2(seg, 2.0));
    float h2 = hash(vec2(seg, 6.0));
    float h3 = hash(vec2(seg, 9.0));

    float st = smoothstep(0.03, 0.85, t);
    float sr = 0.12 + (0.9 + 0.8 * h1) * st;
    float radBand = exp(-pow((r - sr) * (9.0 + 5.0 * h2), 2.0));
    // Angular drift = shard spin while it flies.
    float center = 0.5 + (h3 - 0.5) * 0.5 + (h1 - 0.5) * t * 1.1;
    float angBand = 1.0 - smoothstep(0.0, 0.14 + 0.1 * h2, abs(fr - center));
    float glint = 0.7 + 0.3 * sin(tt * 22.0 + seg * 3.7);
    float fade = 1.0 - smoothstep(0.6, 0.95, t);
    float shard = radBand * angBand * glint * fade;
    float dust = exp(-pow((r - sr * 0.7) * 5.0, 2.0)) * angBand * 0.3 * fade;

    vec3 white = vec3(1.0);
    vec3 mint = vec3(0.063, 0.957, 0.545);
    vec3 col = white * flash + mix(white, mint, 0.45) * shard * 1.5 + mint * dust;
    float alpha = clamp(flash + shard * 1.3 + dust, 0.0, 1.0);
    gl_FragColor = vec4(col, alpha);
  }
`

// Quake! — the ground gives way: an early rumble flicker, a jagged magma
// crack tearing outward along x (with a thinner branch splitting off), and
// three staggered shockwave rings.
export const BRUTALITY_QUAKE_FRAG = /* glsl */ `
  precision mediump float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uDuration;
  uniform vec2 uAspect;
  uniform vec2 uCenter;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  void main() {
    vec2 uv = ((vUv - uCenter) * 2.0) * uAspect;
    float r = length(uv);
    float t = clamp(uTime / uDuration, 0.0, 1.0);
    float tt = uTime;

    // Whole-screen rumble flicker, strongest at the start.
    float flick = (0.5 + 0.5 * hash(vec2(floor(tt * 26.0), 1.0))) * exp(-5.0 * t) * 0.45;

    // Main jagged crack tearing outward from center along x.
    float jag = 0.055 * sin(uv.x * 11.0 + 0.7)
      + 0.035 * sin(uv.x * 23.0 - 1.9)
      + 0.02 * sin(uv.x * 41.0 + 3.1);
    float grow = smoothstep(0.0, 0.34, t) * 1.7;
    float xMask = 1.0 - smoothstep(grow - 0.08, grow, abs(uv.x));
    float mainCrack = (1.0 - smoothstep(0.0, 0.028, abs(uv.y - jag))) * xMask;

    // Thinner branch crack splits off a beat later.
    float jag2 = jag * 1.6 + 0.12 + 0.05 * sin(uv.x * 7.0 - 2.0);
    float grow2 = smoothstep(0.18, 0.5, t) * 1.4;
    float branch = (1.0 - smoothstep(0.0, 0.016, abs(uv.y - jag2)))
      * (1.0 - smoothstep(grow2 - 0.06, grow2, abs(uv.x - 0.25)));

    // Magma glow bleeding out of the main crack.
    float glow = exp(-pow((uv.y - jag) * 5.0, 2.0)) * xMask * (0.5 + 0.2 * sin(tt * 9.0));

    // Three staggered shockwaves.
    float rings = 0.0;
    for (int k = 0; k < 3; k++) {
      float fk = float(k);
      float wt = (t - 0.06 - 0.2 * fk) * 2.4;
      if (wt > 0.0) {
        rings += (1.0 - smoothstep(0.0, 0.045, abs(r - wt))) * (1.0 - 0.25 * fk);
      }
    }

    float fade = 1.0 - smoothstep(0.7, 1.0, t);
    vec3 white = vec3(1.0);
    vec3 mint = vec3(0.063, 0.957, 0.545);
    vec3 col = (white * (mainCrack + branch) * 1.3
      + mint * glow * 0.9
      + mix(white, mint, 0.5) * rings) * fade
      + white * flick;
    float alpha = clamp((mainCrack * 1.2 + branch + glow * 0.8 + rings) * fade + flick, 0.0, 1.0);
    gl_FragColor = vec4(col, alpha);
  }
`
